import { createStore } from "zustand/vanilla";

export const DrawerMode = {
	View: "view",
	Edit: "edit",
} as const;

export type DrawerMode = (typeof DrawerMode)[keyof typeof DrawerMode];

/**
 * Drawer State Interface
 * Manages state for drawer footer including errors, success messages, form state, and button handlers
 */
export interface DrawerState {
	// View / edit mode (local — not reflected in the URL)
	mode: DrawerMode;
	setMode: (mode: DrawerMode) => void;

	// Error state
	errors: string[];
	addError: (error: string) => void;
	clearErrors: () => void;

	// Success state
	successMessages: string[];
	addSuccess: (message: string) => void;
	clearSuccessMessages: () => void;

	// Form state (synced from child routes)
	canSubmit: boolean;
	isDirty: boolean;
	isSubmitting: boolean;
	setFormState: (
		state: Partial<Pick<DrawerState, "canSubmit" | "isDirty" | "isSubmitting">>,
	) => void;

	// Button action handlers (set by child routes)
	handleSave: (() => void | Promise<void>) | null;
	handleCancel: (() => void | Promise<void>) | null;
	setSaveHandler: (handler: (() => void | Promise<void>) | null) => void;
	setCancelHandler: (handler: (() => void | Promise<void>) | null) => void;

	// Reset function (call when drawer closes or route changes)
	reset: () => void;
}

/**
 * Initial state for the drawer
 */
const initialState = {
	mode: DrawerMode.View as DrawerMode,
	errors: [],
	successMessages: [],
	canSubmit: false,
	isDirty: false,
	isSubmitting: false,
	handleSave: null,
	handleCancel: null,
};

/**
 * Factory function to create a new drawer store instance
 * Each drawer instance gets its own isolated store
 */
export const createDrawerStore = () => {
	return createStore<DrawerState>((set) => ({
		...initialState,

		setMode: (mode: DrawerMode) => set({ mode }),

		// Error management
		addError: (error: string) =>
			set((state) => ({ errors: [...state.errors, error] })),

		clearErrors: () => set({ errors: [] }),

		// Success message management
		addSuccess: (message: string) =>
			set((state) => ({
				successMessages: [...state.successMessages, message],
			})),

		clearSuccessMessages: () => set({ successMessages: [] }),

		// Form state management (synced from child routes)
		setFormState: (formState) => set((state) => ({ ...state, ...formState })),

		// Button handler management (set by child routes)
		setSaveHandler: (handler: (() => void | Promise<void>) | null) =>
			set({ handleSave: handler }),

		setCancelHandler: (handler: (() => void | Promise<void>) | null) =>
			set({ handleCancel: handler }),

		// Reset all state to initial values
		reset: () => set(initialState),
	}));
};

/**
 * Type definition for the drawer store
 */
export type DrawerStore = ReturnType<typeof createDrawerStore>;
